import { useEffect, useState } from "react";
import { Sparkle, UsersThree } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import AIDrawer, { useAIStream } from "@/components/AIDrawer";
import { AudienceEngine } from "@/components/AudienceEngine";
import { api, inr, compact } from "@/lib/api";

const STATUS_STYLES = {
  active: "bg-success/10 text-[#00953e] border-success/25",
  fatigued: "bg-warning/10 text-[#b07600] border-warning/30",
  untested: "bg-[#F3F4F6] text-[#525866] border-[#E5E7EB]",
};

export default function Audiences() {
  const [audiences, setAudiences] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const ai = useAIStream();

  useEffect(() => {
    api.get("/audiences").then(({ data }) => setAudiences(data)).catch(() => {});
    api.get("/accounts").then(({ data }) => setAccounts(data)).catch(() => {});
  }, []);

  const accountName = (id) => accounts.find((a) => a.id === id)?.name || id;

  const runInsights = () => {
    setDrawer({ title: "Audience Strategy", subtitle: "Targeting analysis · segments to scale, fatigue alerts, new audiences to test" });
    ai.start({ type: "audience" });
  };

  return (
    <div className="space-y-6">
      <AudienceEngine />
      <div className="flex items-center justify-between">
        <p className="text-sm text-[#525866]">{audiences ? `${audiences.length} audience segments across ${accounts.length} accounts` : " "}</p>
        <Button onClick={runInsights} data-testid="audience-ai-button"
          className="rounded-full bg-ai hover:bg-[#5457e0] text-white font-bold transition-transform hover:-translate-y-[1px] active:scale-[0.98]">
          <Sparkle size={16} weight="fill" className="mr-1.5" /> AI Targeting Suggestions
        </Button>
      </div>

      {!audiences ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-44 rounded-lg" />)}</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {audiences.map((a) => (
            <div key={a.id} data-testid={`audience-card-${a.id}`} className="bg-white border border-[#E5E7EB] rounded-lg p-6 hover:-translate-y-1 hover:border-[#c9cdd6] transition-transform duration-200">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-heading text-sm font-extrabold tracking-tight text-[#0A0D14]">{a.name}</h3>
                  <p className="text-[11px] text-[#8a90a0] mt-0.5">{accountName(a.account_id)}</p>
                </div>
                <Badge className={`rounded-full font-bold text-[10px] uppercase border shrink-0 ${STATUS_STYLES[a.status] || STATUS_STYLES.untested}`}>{a.status}</Badge>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-4">
                {a.interests.map((i) => (
                  <span key={i} className="rounded-full bg-[#F3F4F6] border border-[#E5E7EB] px-2.5 py-0.5 text-[11px] font-semibold text-[#525866]">{i}</span>
                ))}
              </div>
              <div className="grid grid-cols-4 gap-3 mt-5 pt-4 border-t border-[#E5E7EB]/60">
                <div><div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a90a0] flex items-center gap-1"><UsersThree size={11} />Size</div><div className="font-heading font-extrabold text-sm text-[#0A0D14] mt-1">{a.size ? compact(a.size) : "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a90a0]">Age</div><div className="font-heading font-extrabold text-sm text-[#0A0D14] mt-1">{a.age_range}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a90a0]">CPL</div><div className="font-heading font-extrabold text-sm text-[#0A0D14] mt-1">{a.cpl ? inr(a.cpl) : "—"}</div></div>
                <div><div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#8a90a0]">CVR</div><div className="font-heading font-extrabold text-sm text-[#0A0D14] mt-1">{a.cvr ? `${a.cvr}%` : "—"}</div></div>
              </div>
              <div className="mt-3 text-[11px] text-[#8a90a0]">Placement: {a.placement}</div>
            </div>
          ))}
        </div>
      )}

      <AIDrawer open={!!drawer} onClose={() => { setDrawer(null); ai.reset(); }} title={drawer?.title} subtitle={drawer?.subtitle} text={ai.text} running={ai.running} error={ai.error} />
    </div>
  );
}
